/**
 * Global axios interceptor — Session 213.85
 *
 * Detects 401 Unauthorized responses on any auth-protected API call,
 * which usually means the user's session cookie (``access_token``)
 * has expired silently. Without this, users see a cryptic "Failed to
 * save / load" error and have no idea why; the dev console fills up
 * with anonymous 401s on background pollers like ``/notifications/count``.
 *
 * On 401:
 *   1. Skip the noise paths (login attempt 401, public branding, etc.)
 *   2. De-dupe so we only show the "session expired" toast once even
 *      if 10 background fetches all 401 simultaneously
 *   3. Clear cached user state from AuthContext (via `unauth` callback)
 *   4. Show a clear toast
 *   5. Redirect to /login?next=<current_path> so the user lands back
 *      where they were after re-authenticating
 *
 * Always wire this up exactly once at app startup. Calling
 * ``installAuthExpiryInterceptor`` multiple times is safe — extra
 * registrations are no-ops via the ``_installed`` flag.
 */
import axios from 'axios';
import { toast } from 'sonner';

let _installed = false;
let _toastShown = false;

// Paths whose 401 should NOT trigger the "session expired" redirect.
// (Login attempts produce 401 on bad credentials; that error belongs
// to the login form, not the global handler. ``/auth/me`` polls auth
// state on startup; a 401 there just means "not logged in yet".)
const NOISE_PATTERNS = [
  /\/api\/auth\/login/,
  /\/api\/auth\/me/,
  /\/api\/auth\/register/,
  /\/api\/auth\/logout/,
  /\/api\/auth\/forgot-password/,
  /\/api\/auth\/reset-password/,
  /\/api\/branding/,
  /\/api\/site-content/,         // public CMS read
  /\/api\/insured\/auth\//,      // separate insured portal flow
  /\/api\/platform\//,           // separate platform admin flow
];

const _isNoise = (url) => {
  if (!url) return false;
  return NOISE_PATTERNS.some((rx) => rx.test(url));
};

export function installAuthExpiryInterceptor({ onSessionExpired } = {}) {
  if (_installed) return;
  _installed = true;

  axios.interceptors.response.use(
    (resp) => resp,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';
      if (status === 401 && !_isNoise(url)) {
        if (!_toastShown) {
          _toastShown = true;
          toast.error('Your session expired. Please log in again.', {
            id: 'session-expired',
            duration: 6000,
          });
          // Allow another toast 30s later if user lingers without re-auth
          setTimeout(() => { _toastShown = false; }, 30000);
        }
        // Redirect FIRST, before AuthContext flips — otherwise
        // ProtectedRoute re-renders and snaps the user to "/" before
        // we can preserve the original path in ?next=.
        if (typeof window !== 'undefined') {
          const here = window.location.pathname + window.location.search;
          const isLoginPage = window.location.pathname.startsWith('/login');
          if (!isLoginPage) {
            window.location.replace(`/login?next=${encodeURIComponent(here)}`);
            // Return a never-resolving promise so the caller doesn't
            // try to handle the 401 itself while we're navigating away.
            return new Promise(() => {});
          }
        }
        // Clear cached user state from AuthContext (silently swallow).
        // Only reached if we couldn't navigate (e.g. SSR / Jest).
        try { onSessionExpired?.(); } catch { /* noop */ }
      }
      return Promise.reject(error);
    },
  );
}
